import { useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Home,
  Users,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Sparkles,
  X,
  ArrowRight,
} from "lucide-react";

interface OnboardingStatusResponse {
  completed: boolean;
  orgName?: string;
  steps?: {
    hasBuildings: boolean;
    hasApartments: boolean;
    hasTeamMembers: boolean;
  };
}

export default function OrgOnboardingGuard({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export function OnboardingChecklist() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const { data, isLoading } = useQuery<OnboardingStatusResponse>({
    queryKey: ["/api/admin/onboarding-status"],
  });

  const skipOnboarding = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/admin/complete-onboarding");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/onboarding-status"] });
      setDismissed(true);
    },
  });

  if (isLoading || !data || data.completed || dismissed) return null;

  const steps = [
    {
      key: "building",
      label: "Add your first property",
      description: "A property is a building or complex that contains your rental units",
      icon: Building2,
      completed: data.steps?.hasBuildings || false,
    },
    {
      key: "apartment",
      label: "Create a rental unit",
      description: "A unit is an individual apartment, room, or rental space within a property",
      icon: Home,
      completed: data.steps?.hasApartments || false,
      requiresPrevious: !data.steps?.hasBuildings,
    },
    {
      key: "team",
      label: "Invite a team member",
      description: "Add cleaners, maintenance staff, or property managers to your team",
      icon: Users,
      completed: data.steps?.hasTeamMembers || false,
    },
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const allDone = completedCount === steps.length;

  return (
    <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-background to-amber-50/30 dark:to-neutral-900" data-testid="onboarding-checklist">
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-base" data-testid="text-onboarding-title">
                {allDone ? "You're all set!" : `Getting started with ${data.orgName || "Staio"}`}
              </h3>
              <p className="text-sm text-muted-foreground">
                {allDone
                  ? "Your account is ready. You can dismiss this guide."
                  : `${completedCount} of ${steps.length} steps completed`}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground shrink-0"
            onClick={() => skipOnboarding.mutate()}
            disabled={skipOnboarding.isPending}
            data-testid="button-onboarding-dismiss"
          >
            {skipOnboarding.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex gap-1.5 mb-4">
          {steps.map((step) => (
            <div
              key={step.key}
              className={`h-1.5 flex-1 rounded-full transition-colors ${step.completed ? "bg-primary" : "bg-muted"}`}
            />
          ))}
        </div>

        <div className="space-y-2">
          {steps.map((step) => (
            <StepItem
              key={step.key}
              step={step}
              isExpanded={expandedStep === step.key}
              onToggle={() => setExpandedStep(expandedStep === step.key ? null : step.key)}
              onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ["/api/admin/onboarding-status"] });
                queryClient.invalidateQueries({ queryKey: ["/api/buildings"] });
                queryClient.invalidateQueries({ queryKey: ["/api/apartments"] });
                queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
                setExpandedStep(null);
              }}
            />
          ))}
        </div>

        {allDone && (
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              onClick={() => skipOnboarding.mutate()}
              disabled={skipOnboarding.isPending}
              data-testid="button-onboarding-finish"
            >
              {skipOnboarding.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Dismiss Guide <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StepItem({
  step,
  isExpanded,
  onToggle,
  onComplete,
}: {
  step: { key: string; label: string; description: string; icon: any; completed: boolean; requiresPrevious?: boolean };
  isExpanded: boolean;
  onToggle: () => void;
  onComplete: () => void;
}) {
  const Icon = step.icon;
  const disabled = step.requiresPrevious;

  return (
    <div
      className={`rounded-lg border transition-colors ${
        step.completed
          ? "bg-primary/5 border-primary/20"
          : disabled
          ? "bg-muted/30 border-border/50 opacity-60"
          : "bg-background border-border hover:border-primary/30"
      }`}
    >
      <button
        onClick={disabled ? undefined : onToggle}
        className={`w-full flex items-center gap-3 p-3 text-left ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={`step-${step.key}`}
        disabled={disabled}
      >
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${step.completed ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          {step.completed ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${step.completed ? "line-through text-muted-foreground" : ""}`}>{step.label}</p>
          {!isExpanded && <p className="text-xs text-muted-foreground truncate">{step.description}</p>}
        </div>
        {!step.completed && !disabled && (
          isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {isExpanded && !step.completed && (
        <div className="px-3 pb-3 pt-1 border-t">
          {step.key === "building" && <InlineBuildingForm onComplete={onComplete} />}
          {step.key === "apartment" && <InlineApartmentForm onComplete={onComplete} />}
          {step.key === "team" && <InlineTeamForm onComplete={onComplete} />}
        </div>
      )}
    </div>
  );
}

function InlineBuildingForm({ onComplete }: { onComplete: () => void }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");

  const createBuilding = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/buildings", { name, address });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Property created!" });
      onComplete();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ob-building-name" className="text-xs">Property Name</Label>
          <Input
            id="ob-building-name"
            placeholder="e.g. Sunset Apartments"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-building-name"
          />
        </div>
        <div>
          <Label htmlFor="ob-building-address" className="text-xs">Address</Label>
          <Input
            id="ob-building-address"
            placeholder="e.g. 123 Main St, Miami, FL"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-building-address"
          />
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => createBuilding.mutate()}
        disabled={!name || !address || createBuilding.isPending}
        data-testid="button-onboarding-create-building"
      >
        {createBuilding.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Building2 className="w-4 h-4 mr-1" />}
        Create Property
      </Button>
    </div>
  );
}

function InlineApartmentForm({ onComplete }: { onComplete: () => void }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [unitNumber, setUnitNumber] = useState("");

  const { data: buildings = [] } = useQuery<any[]>({
    queryKey: ["/api/buildings"],
  });

  const createApartment = useMutation({
    mutationFn: async () => {
      const buildingId = buildings.length > 0 ? buildings[0].id : undefined;
      const res = await apiRequest("POST", "/api/apartments", {
        name,
        unitNumber: unitNumber || undefined,
        buildingId,
        status: "available",
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Unit created!" });
      onComplete();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-3 pt-2">
      {buildings.length > 0 && (
        <p className="text-xs text-muted-foreground">Adding to: <span className="font-medium text-foreground">{buildings[0].name}</span></p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ob-apt-name" className="text-xs">Unit Name</Label>
          <Input
            id="ob-apt-name"
            placeholder="e.g. Studio A, Suite 101"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-apartment-name"
          />
        </div>
        <div>
          <Label htmlFor="ob-apt-number" className="text-xs">Unit # <span className="text-muted-foreground">(optional)</span></Label>
          <Input
            id="ob-apt-number"
            placeholder="e.g. 101"
            value={unitNumber}
            onChange={(e) => setUnitNumber(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-apartment-number"
          />
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => createApartment.mutate()}
        disabled={!name || createApartment.isPending}
        data-testid="button-onboarding-create-apartment"
      >
        {createApartment.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Home className="w-4 h-4 mr-1" />}
        Create Unit
      </Button>
    </div>
  );
}

function InlineTeamForm({ onComplete }: { onComplete: () => void }) {
  const { toast } = useToast();
  const [fullName, setFullName] = useState("");
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("cleaner");

  const inviteMember = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/users", {
        fullName,
        username,
        password: "changeme123",
        role,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Team member added!", description: "Default password: changeme123" });
      setFullName("");
      setUsername("");
      onComplete();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="space-y-3 pt-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ob-member-name" className="text-xs">Full Name</Label>
          <Input
            id="ob-member-name"
            placeholder="e.g. Maria Garcia"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-member-name"
          />
        </div>
        <div>
          <Label htmlFor="ob-member-username" className="text-xs">Username</Label>
          <Input
            id="ob-member-username"
            placeholder="e.g. maria.garcia"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="h-9"
            data-testid="input-onboarding-member-username"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs">Role</Label>
        <div className="flex gap-2 mt-1">
          {[
            { value: "cleaner", label: "Cleaner" },
            { value: "maintenance", label: "Maintenance" },
            { value: "owner", label: "Property Manager" },
          ].map((r) => (
            <Button
              key={r.value}
              type="button"
              variant={role === r.value ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setRole(r.value)}
              data-testid={`button-onboarding-role-${r.value}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Default password: <span className="font-mono bg-muted px-1 rounded text-xs">changeme123</span></p>
      <Button
        size="sm"
        onClick={() => inviteMember.mutate()}
        disabled={!fullName || !username || inviteMember.isPending}
        data-testid="button-onboarding-invite-member"
      >
        {inviteMember.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Users className="w-4 h-4 mr-1" />}
        Add Team Member
      </Button>
    </div>
  );
}
